import {
  Typography,
  Tooltip,
  TooltipTrigger,
  Button,
  DialogTrigger,
  Popover,
} from "@repo/design-system";

type ICellValue = string | string[] | number;

interface ITruncateConfig {
  maxChars: number;
  truncateTo: number;
}

interface IAnalysisTableProps {
  title: string;
  columns: string[];
  rows: ICellValue[][];
  wrapCols?: Record<number, number>;
  headerOnlyCols?: number[];
  centerCols?: number[];
  truncateCols?: Record<number, ITruncateConfig>;
}

function formatCell(value: ICellValue): string {
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

function TruncatedCell({
  value,
  extraClass,
  maxChars,
  truncateTo,
}: {
  value: ICellValue;
  extraClass: string;
  maxChars: number;
  truncateTo: number;
}) {
  const text = formatCell(value);
  const isLong = text.length > maxChars;
  const display = isLong ? text.slice(0, truncateTo) + "..." : text;

  if (!isLong) {
    return (
      <Typography
        variant="paragraph-l-regular"
        className={`text-contentPrimary${extraClass}`}>
        {display}
      </Typography>
    );
  }

  return (
    <>
      {/* Desktop: hover tooltip */}
      <div className="hidden md:block">
        <TooltipTrigger delay={200}>
          <Button
            variant="transparent"
            size="small"
            className="p-0 min-h-0 h-auto">
            <Typography
              variant="paragraph-l-regular"
              className={`text-contentPrimary${extraClass}`}>
              {display}
            </Typography>
          </Button>
          <Tooltip>{text}</Tooltip>
        </TooltipTrigger>
      </div>
      {/* Mobile: tap popover */}
      <div className="md:hidden">
        <DialogTrigger>
          <Button
            variant="transparent"
            size="small"
            className="p-0 min-h-0 h-auto">
            <Typography
              variant="paragraph-l-regular"
              className={`text-contentPrimary${extraClass}`}>
              {display}
            </Typography>
          </Button>
          <Popover showArrow>
            <div className="max-w-xs p-3">
              <Typography
                variant="paragraph-m-regular"
                className="text-contentPrimary">
                {text}
              </Typography>
            </div>
          </Popover>
        </DialogTrigger>
      </div>
    </>
  );
}

function computeColInfo(
  columns: string[],
  rows: ICellValue[][],
  wrapCols: Record<number, number>,
  headerOnlyCols: number[],
  truncateCols: Record<number, ITruncateConfig>,
): { template: string; wrapped: boolean[] } {
  const headerOnlySet = new Set(headerOnlyCols);

  const template = columns
    .map((col, colIdx) => {
      if (colIdx in wrapCols) {
        const cfg = truncateCols[colIdx];
        if (cfg) {
          return `${Math.max(col.length, cfg.truncateTo + 3)}ch`;
        }
        const maxItemLen = rows.reduce<number>((max, row) => {
          const cell = row[colIdx];
          if (Array.isArray(cell)) {
            const arrMax = (cell as (string | number)[]).reduce<number>(
              (m, item) => Math.max(m, String(item ?? "").length),
              0,
            );
            return Math.max(max, arrMax);
          }
          return Math.max(max, String(cell ?? "").length);
        }, col.length);
        return `${Math.min(maxItemLen, 30)}ch`;
      }
      if (headerOnlySet.has(colIdx)) return `${col.length}ch`;
      const maxDataLen = rows.reduce((max, row) => {
        const cellStr = formatCell(row[colIdx] ?? "");
        return Math.max(max, cellStr.length);
      }, 0);
      return `${Math.max(col.length, maxDataLen)}ch`;
    })
    .join(" ");

  const wrapped = columns.map((_, i) => i in wrapCols);

  return { template, wrapped };
}

export function AnalysisTable({
  title,
  columns,
  rows,
  wrapCols = {},
  headerOnlyCols = [],
  centerCols = [],
  truncateCols = {},
}: IAnalysisTableProps) {
  const { template, wrapped } = computeColInfo(
    columns,
    rows,
    wrapCols,
    headerOnlyCols,
    truncateCols,
  );
  const centerSet = new Set(centerCols);

  return (
    <div className="flex flex-col gap-4">
      <Typography
        variant="heading-4"
        className="text-center text-contentPrimary">
        {title}
      </Typography>
      <div className="overflow-hidden rounded-xl border border-borderPrimary">
        <div className="overflow-x-auto">
          <div
            className="grid gap-4 border-b border-borderPrimary bg-backgroundSecondary px-4 py-3"
            style={{ gridTemplateColumns: template }}>
            {columns.map((col) => (
              <Typography
                key={col}
                variant="paragraph-m-semibold"
                className="text-contentSecondary">
                {col}
              </Typography>
            ))}
          </div>
          {rows.length === 0 ? (
            <div className="flex items-center justify-center py-10">
              <Typography
                variant="paragraph-l-medium"
                className="text-contentSecondary">
                No data available
              </Typography>
            </div>
          ) : (
            rows.map((row, rowIdx) => (
              <div
                key={rowIdx}
                className="grid items-center gap-4 border-b border-borderPrimary px-4 py-3 last:border-b-0 transition-colors hover:bg-backgroundSecondary"
                style={{ gridTemplateColumns: template }}>
                {row.map((cell, cellIdx) => {
                  const extraClass = `${wrapped[cellIdx] ? " break-words" : ""}${centerSet.has(cellIdx) ? " text-center" : ""}`;
                  const truncateCfg = truncateCols[cellIdx];
                  if (truncateCfg) {
                    return (
                      <TruncatedCell
                        key={cellIdx}
                        value={cell}
                        extraClass={extraClass}
                        maxChars={truncateCfg.maxChars}
                        truncateTo={truncateCfg.truncateTo}
                      />
                    );
                  }
                  if (wrapped[cellIdx] && Array.isArray(cell)) {
                    return (
                      <div key={cellIdx} className="flex flex-col gap-0.5">
                        {(cell as string[]).map((item, i) => (
                          <Typography
                            key={i}
                            variant="paragraph-l-regular"
                            className="text-contentPrimary">
                            {String(item)}
                          </Typography>
                        ))}
                      </div>
                    );
                  }
                  return (
                    <Typography
                      key={cellIdx}
                      variant="paragraph-l-regular"
                      className={`text-contentPrimary${extraClass}`}>
                      {formatCell(cell)}
                    </Typography>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
